// Pure list helpers for the participant grid: alphabetical sort, name search,
// and the A-Z jump index. Kept free of React/FHIR so they are unit-testable on
// plain objects; a real `Participant` satisfies `SortableParticipant`
// structurally.

export interface SortableParticipant {
  id: string
  // Full display name (first + last), used for search matching.
  name: string
  firstName: string
  lastName: string
  nickname?: string
}

export const ALPHABET = Array.from({ length: 26 }, (_, i) =>
  String.fromCharCode(65 + i)
)

// Everything that isn't A-Z (empty/unknown/CJK last names) groups under '#'.
export const NON_ALPHA_LETTER = '#'

// The A-Z index letter for a participant, derived from their last name (the
// sort key). Non-alphabetic first characters fall under '#'.
export function indexLetterFor(participant: SortableParticipant): string {
  const first = participant.lastName.trim().charAt(0).toUpperCase()
  return first >= 'A' && first <= 'Z' ? first : NON_ALPHA_LETTER
}

function letterGroupRank(participant: SortableParticipant): number {
  const letter = indexLetterFor(participant)
  return letter === NON_ALPHA_LETTER
    ? ALPHABET.length
    : letter.charCodeAt(0) - 65
}

// Sort A–Z last-name groups first, then the '#' bucket, matching the rail.
// Within a group: last name, then first name — the roster convention used
// elsewhere (e.g. PractitionerTable). Case-insensitive so a lowercase-entered
// name still groups with its letter and the first-name tiebreak fires reliably.
// Grouping before localeCompare keeps digit/CJK/accented last names in one
// '#' block; otherwise they scatter and the rail only reaches the first island.
export function sortParticipantsByName<T extends SortableParticipant>(
  participants: T[]
): T[] {
  return [...participants].sort((a, b) => {
    const group = letterGroupRank(a) - letterGroupRank(b)
    if (group !== 0) {
      return group
    }
    return (
      a.lastName.toLowerCase().localeCompare(b.lastName.toLowerCase()) ||
      a.firstName.toLowerCase().localeCompare(b.firstName.toLowerCase())
    )
  })
}

// Case-insensitive, whitespace-tokenized name search: every term must appear in
// the participant's name or nickname, so "smith john" and "john smith" both
// match. A blank query returns the list unchanged.
export function filterParticipantsByQuery<T extends SortableParticipant>(
  participants: T[],
  query: string
): T[] {
  const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean)
  if (terms.length === 0) {
    return participants
  }
  return participants.filter((participant) => {
    const haystack =
      `${participant.name} ${participant.nickname ?? ''}`.toLowerCase()
    return terms.every((term) => haystack.includes(term))
  })
}

// The set of index letters that actually have participants, so the rail can dim
// letters that would scroll nowhere.
export function availableLetters(
  participants: SortableParticipant[]
): Set<string> {
  return new Set(participants.map(indexLetterFor))
}
